import React, { useEffect, useState } from 'react';
import Drawer from '../components/Drawer';
import Footer from '../components/Footer';
import { getOrgMembers, removeMember, toggleMember, updateMemberRole } from '../api/api';
import { useAuth } from '../context/AuthContext';
import StyledSelect from '../components/StyledSelect';

const roles = ['admin', 'finance_manager', 'accountant', 'project_manager', 'employee', 'viewer'];

export default function OrganizationMembersPage() {
  const { activeOrg, canManageMembers, canViewUsers, canEditUsers, canSuspendUsers } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadMembers = async () => {
    if (!activeOrg?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await getOrgMembers(activeOrg.id, { page: 1, limit: 100 });
      setMembers(res?.data?.data?.members || []);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load organization members.');
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canViewUsers) return;
    loadMembers();
  }, [activeOrg?.id, canViewUsers]);

  if (!canViewUsers) {
    const denied = (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-3xl border bg-white p-6 text-center" style={{ borderColor: '#f2d8e0' }}>
          <h1 className="text-2xl font-bold text-slate-900">Organization Members</h1>
          <p className="mt-2 text-sm text-slate-500">You do not have permission to view organization members.</p>
        </div>
      </div>
    );

    return <><Drawer pageHeader={denied} /><Footer /></>;
  }

  const onRoleChange = async (userId, orgRole) => {
    await updateMemberRole(activeOrg.id, userId, { org_role: orgRole });
    await loadMembers();
  };

  const onToggle = async (userId, isActive) => {
    await toggleMember(activeOrg.id, userId, { is_active: !isActive });
    await loadMembers();
  };

  const onRemove = async (userId) => {
    if (!window.confirm('Remove this member from the organization?')) return;
    await removeMember(activeOrg.id, userId);
    await loadMembers();
  };

  const pageContent = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 space-y-6">
      <section className="rounded-3xl border p-6 shadow-sm" style={{ borderColor: '#f4d6df', background: '#fff' }}>
        <h1 className="text-3xl font-bold text-slate-900">Organization Members</h1>
        <p className="text-sm text-slate-500 mt-2">Review membership, adjust roles, and control tenant access from one place.</p>
      </section>

      {error ? <Alert text={error} /> : null}

      <section className="rounded-3xl border p-5 shadow-sm overflow-auto" style={{ borderColor: '#f4d6df', background: '#fff' }}>
        {loading ? <p className="text-sm text-slate-500">Loading members...</p> : null}
        {!loading && (
          <table className="w-full min-w-[920px] text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-100">
                <th className="py-3">Member</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Status</th>
                <th>Joined</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.id} className="border-b border-slate-50">
                  <td className="py-3">
                    <p className="font-semibold text-slate-900">{member.name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'User'}</p>
                    <p className="text-xs text-slate-400">Last login: {member.last_login_at || 'Never'}</p>
                  </td>
                  <td>{member.email}</td>
                  <td>{member.phone || 'n/a'}</td>
                  <td>
                    {canManageMembers && canEditUsers ? (
                      <div className="min-w-[170px]">
                        <StyledSelect
                          value={member.org_role}
                          onChange={(next) => onRoleChange(member.id, next)}
                          options={roles.map((role) => ({ value: role, label: role.replace('_', ' ') }))}
                          placeholder="Role"
                          searchable
                        />
                      </div>
                    ) : (
                      <span className="font-medium text-slate-700">{member.org_role}</span>
                    )}
                  </td>
                  <td>
                    <span className="px-2 py-1 rounded-full text-xs font-semibold" style={{
                      background: member.is_active ? '#dcfce7' : '#fee2e2',
                      color: member.is_active ? '#166534' : '#991b1b',
                    }}>
                      {member.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{member.joined_at || '-'}</td>
                  <td className="text-right">
                    {canManageMembers ? (
                      <div className="inline-flex gap-2">
                        <button disabled={!canSuspendUsers} onClick={() => onToggle(member.id, member.is_active)} className="px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-50" style={{ background: '#fdf0f4', color: '#c44569' }}>
                          {member.is_active ? 'Disable' : 'Enable'}
                        </button>
                        <button disabled={!canSuspendUsers} onClick={() => onRemove(member.id)} className="px-3 py-2 rounded-xl text-xs font-semibold disabled:opacity-50" style={{ background: '#fee2e2', color: '#dc2626' }}>
                          Remove
                        </button>
                      </div>
                    ) : (
                      <span className="text-slate-400">Read only</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );

  return <><Drawer pageHeader={pageContent} /><Footer /></>;
}

function Alert({ text }) {
  return <div className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: '#f9b8ca', background: '#fce0e8', color: '#c44569' }}>{text}</div>;
}
